/**
 * Test names are awful! They're hard to parse out and unpredictable,
 * but this logic was written in collaboration with @dave-connors-3
 * in order to best understand the dbt context of what a user would expect
 *
 * This logic can totally change in the future, but this was a best-effort
 * stab at getting reasonable test names.
 */

import { useMemo } from 'react';

import { TestType } from '@/__generated__/graphql';

/**
 * Custom-named tests do not have a hash. We can take the third part of the uniqueId
 */
export const getCustomTestName = (uniqueId: string) => {
  const parts = uniqueId.split('.');
  // not considered custom test
  // This will break if package or custom test name contains .
  if (parts.length > 3) {
    return undefined;
  }
  // Example test failure: "test.package.test.with.a.period"
  // Example success: "test.package.custom_name"
  return parts[2];
};

/**
 * The `model` field on testMetadata is actually the referenced
 * resource that we are running tests against (and doesn't have to be a model).
 * Anecdotally, we saw data like ref("model.pkg.name"), and so this regex is there
 * to handle different ref inputs.
 *
 * If we fail to match to any ref, then we just return the whole string.
 */
const parseModelField = (modelStr: string | null) => {
  if (!modelStr) return null;
  const refRe = /(\sdbt\.)*ref\([\n'"`]*([^'"`]*)['"`\n]*[^)]*\)/;

  const arr = refRe.exec(modelStr);

  const reference = arr ? arr[arr.length - 1] : modelStr;
  return reference;
};

/**
 * We're assuming the only two kwargs keys that should always be there,
 * `model` and `column_name`. `model` is poorly named, because its
 * not always a model, and needs to be handled by parseModelField
 */
const parseTestKwargs = (kwargs: any, outerColumnName: string | null | undefined) => {
  if (!kwargs) return null;
  const ref = parseModelField(kwargs.model);
  const columnName: string | null =
    (typeof kwargs.column_name === 'string' || kwargs.column_name instanceof String
      ? kwargs.column_name
      : outerColumnName
    )?.toLocaleLowerCase() || null;

  if (!ref) return null;
  return { ref, columnName };
};

interface TestNameAccessors {
  /** Should only be used when there is no context */
  fullName: string;
  /** Should only be used in the context of the tested resource */
  mediumName: string;
  /** Should only be used in a high context area (a badge in a detail row) */
  shortName: string;
}

type TestNameWithoutAccessors = {
  /** Item we are running a test on */
  ref?: string;
  /** The column in the ref we are testing against */
  columnName?: string | null;
  /**
   * The name of the test, which can be custom.
   * This short name should likely appear in the context
   * of the ref and column name.
   */
  name: string;
  customName: string | null;
  macroName: string | null;
};

export type ParsedTestName = TestNameAccessors & TestNameWithoutAccessors;

export interface TestMetadataKwargs {
  model: string | null;
  column_name: string | null;
  values?: string[] | null;
}

export interface TestNameData {
  uniqueId: string;
  name?: string | null;
  columnName?: string | null;
  testType?: TestType | null;
  model?: string | null;
  testMetadata?: {
    /** macro name if not a custom test */
    name?: string | null;
    kwargs?: TestMetadataKwargs | null;
  } | null;
}

const getNames = (parsedTestName: TestNameWithoutAccessors): TestNameAccessors => {
  const shortName = parsedTestName.name;
  const mediumName = parsedTestName.columnName
    ? `${parsedTestName.columnName}.${shortName}`
    : parsedTestName.ref
      ? `${parsedTestName.ref}.${shortName}`
      : shortName;
  const fullName = parsedTestName.columnName
    ? parsedTestName.ref
      ? `${parsedTestName.ref}.${mediumName}`
      : mediumName
    : mediumName;
  return { shortName, mediumName, fullName };
};

export const parseTestName = (
  testNameData: Partial<TestNameData> | undefined | null,
): ParsedTestName | null => {
  if (!testNameData) return null;
  if (testNameData.testType === TestType.UnitTest) {
    return {
      name: testNameData.name || '',
      fullName: testNameData.name || '',
      mediumName: testNameData.name || '',
      shortName: testNameData.name || '',
      ref: testNameData.model || '',
      customName: null,
      macroName: null,
    };
  }
  const uniqueId = testNameData.uniqueId;
  if (!uniqueId) return null;

  const customName = getCustomTestName(uniqueId)?.toLocaleLowerCase();
  const macroName = customName
    ? null
    : testNameData.testMetadata?.name?.toLocaleLowerCase();
  const uniqueIdRemainder = uniqueId.split('.').slice(2).join('.');
  const name = customName || macroName || uniqueIdRemainder;
  const kwargs = parseTestKwargs(
    testNameData.testMetadata?.kwargs,
    testNameData.columnName,
  );
  if (customName) {
    const parsedTestName = { name, ...kwargs, customName, macroName: null };
    return { ...parsedTestName, ...getNames(parsedTestName) };
  } else if (macroName) {
    const parsedTestName = { name, ...kwargs, customName: null, macroName };
    return { ...parsedTestName, ...getNames(parsedTestName) };
  }
  const parsedTestName = { name, ...kwargs, customName: null, macroName: null };
  return { ...parsedTestName, ...getNames(parsedTestName) };
};

export const useParsedTestName = (testNameData: TestNameData | null | undefined) => {
  return useMemo(() => {
    if (!testNameData) return null;
    return parseTestName(testNameData);
  }, [testNameData]);
};

export const useParsedTestNames = (
  testNameDataArr: TestNameData[] | null | undefined,
) => {
  return useMemo(() => {
    if (!testNameDataArr) return null;
    return testNameDataArr.map((data) => parseTestName(data));
  }, [testNameDataArr]);
};

export const translateTestType = (testType: TestType | null): string => {
  switch (testType) {
    case TestType.UnitTest:
      return 'Unit';
    default:
      return 'Data';
  }
};
